import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  Box,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AuthLayout from '../components/AuthLayout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const ProfilePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  borderRadius: '8px'
}));

const LargeAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  fontSize: 40,
  backgroundColor: '#2e7d32',
  marginBottom: theme.spacing(2)
}));

const Profile: React.FC = () => {
  const { currentUser } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    if (currentUser) {
      setEmail(currentUser.email || '');
      setDisplayName(currentUser.displayName || '');
      
      // Fetch additional user data from backend
      fetchUserProfile();
    }
  }, [currentUser]);
  
  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/auth/user-profile');
      
      if (response.data && response.data.user) {
        setDisplayName(response.data.user.displayName || '');
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      
      // Update profile in backend
      await api.put('/api/auth/user-profile', {
        displayName
      });
      
      setSuccess(true);
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError('Falha ao atualizar perfil. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseSnackbar = () => {
    setSuccess(false);
  };
  
  return (
    <AuthLayout title="Perfil">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 500 }}>
          Meu Perfil
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gerencie suas informações pessoais
        </Typography>
      </Box>
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <ProfilePaper>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <LargeAvatar>
                {displayName ? displayName.charAt(0).toUpperCase() : email.charAt(0).toUpperCase()}
              </LargeAvatar>
              <Typography variant="h5" sx={{ mb: 1 }}>
                {displayName || 'Usuário'}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {email}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Membro desde: {currentUser?.metadata?.creationTime ? new Date(currentUser.metadata.creationTime).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
          </ProfilePaper>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <ProfilePaper>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Editar Informações
            </Typography>
            
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            
            <Box component="form" onSubmit={handleUpdateProfile}>
              <TextField
                fullWidth
                label="Nome"
                margin="normal"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                sx={{ mb: 3 }}
              />
              
              <TextField
                fullWidth
                label="Email"
                margin="normal"
                value={email}
                disabled
                sx={{ mb: 3 }}
              />
              
              <Divider sx={{ my: 3 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  sx={{ 
                    minWidth: 120,
                    backgroundColor: '#2e7d32',
                    '&:hover': {
                      backgroundColor: '#1b5e20',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Salvar'}
                </Button>
              </Box>
            </Box>
          </ProfilePaper>
        </Grid>
      </Grid>
      
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Perfil atualizado com sucesso!
        </Alert>
      </Snackbar>
    </AuthLayout>
  );
};

export default Profile;
